/**
 * Het dossier van een doelpand (MA-56, stap 1 en 2 van de taxatie): wat open
 * bronnen per adres zeggen, naast wat Funda zei.
 *
 * Elke bron staat los. Valt er één weg - een server die niet antwoordt, een
 * adres dat er niet in staat - dan staat er bij die ene "fout" of "geen", met
 * de link om het zelf na te kijken, en de rest gewoon ingevuld. Niets hiervan
 * komt in de database: de API haalt het op en houdt het een halve dag vast.
 */
import { z } from 'zod';

export const BRONSTATUSSEN = ['ok', 'geen', 'fout'] as const;

/** Eén bron: wat hij zei, of waarom niet, en waar je het zelf ziet. */
export function bronSchema<T extends z.ZodType>(waarde: T) {
  return z.object({
    status: z.enum(BRONSTATUSSEN),
    waarde: waarde.nullable(),
    melding: z.string().nullable(),
    link: z.string().nullable(),
  });
}
export type Bron<T> = {
  status: 'ok' | 'geen' | 'fout';
  waarde: T | null;
  melding: string | null;
  link: string | null;
};

const getal = z.number().nullable();

export const STRAATTYPEN = [
  'gracht of kade',
  'winkelstraat',
  'tramstraat',
  'doorgaande weg',
  'rustige woonstraat',
] as const;
export type Straattype = (typeof STRAATTYPEN)[number];

export const GRONDEIGENAREN = ['gemeente', 'vve', 'corporatie', 'particulier', 'overig'] as const;
export const GRONDEIGENAAR_LABELS: Record<(typeof GRONDEIGENAREN)[number], string> = {
  gemeente: 'Gemeente Amsterdam',
  vve: 'Vereniging van eigenaren',
  corporatie: 'Woningcorporatie',
  particulier: 'Particulier',
  overig: 'Overig',
};

export const dossierSchema = z.object({
  adres: z
    .object({
      weergavenaam: z.string(),
      verblijfsobjectId: z.string().nullable(),
      nummeraanduidingId: z.string().nullable(),
      pandId: z.string().nullable(),
      buurtcode: z.string().nullable(),
      buurtnaam: z.string().nullable(),
      wijknaam: z.string().nullable(),
      percelen: z.array(z.string()),
    })
    .nullable(),
  bronnen: z.object({
    /** Landelijke BAG, het pand. */
    bag: bronSchema(
      z.object({
        bouwjaar: getal,
        gebruiksdoel: z.string().nullable(),
        woningenInPand: getal,
        oppervlakteMin: getal,
        oppervlakteMax: getal,
      }),
    ),
    /** De Amsterdamse aanvulling op de BAG, de woning zelf. */
    bagAmsterdam: bronSchema(
      z.object({
        verdieping: getal,
        kamers: getal,
        oppervlakte: getal,
        ligging: z.string().nullable(),
        typeWoonobject: z.string().nullable(),
      }),
    ),
    driedbag: bronSchema(
      z.object({ bouwlagen: getal, hoogteM: getal, daktype: z.string().nullable() }),
    ),
    woz: bronSchema(
      z.object({ reeks: z.array(z.object({ jaar: z.number(), waarde: z.number() })) }),
    ),
    grond: bronSchema(
      z.object({ eigenaar: z.enum(GRONDEIGENAREN), erfpachtWaarschijnlijk: z.boolean() }),
    ),
    monument: bronSchema(z.object({ status: z.string(), nummer: getal })),
    bestemming: bronSchema(
      z.object({
        naam: z.string(),
        hoofdgroep: z.string().nullable(),
        plan: z.string().nullable(),
        datum: z.string().nullable(),
      }),
    ),
    winkelgebied: bronSchema(z.object({ naam: z.string(), categorie: z.string().nullable() })),
    water: bronSchema(z.object({ afstandM: z.number() })),
    /** Straattype en drukte, uit de ingelezen kaartlagen (verkeersprognose, hoofdnetten, tram). */
    straat: bronSchema(
      z.object({
        straatnaam: z.string().nullable(),
        type: z.enum(STRAATTYPEN),
        redenen: z.array(z.string()),
        etmaal: getal,
        netten: z.array(z.string()),
        tram: z.object({ lijnen: z.string().nullable(), afstandM: z.number() }).nullable(),
        halte: z
          .object({ naam: z.string(), lijnen: z.string().nullable(), afstandM: z.number() })
          .nullable(),
      }),
    ),
    /** Corporatiebezit (AFWC 2025): het eigen pand of blok, en wat er in de directe buurt ligt. */
    corporatie: bronSchema(
      z.object({
        inPand: z
          .object({
            corporatie: z.string().nullable(),
            corporatieWoningen: getal,
            woningenInBlok: getal,
            perc: getal,
          })
          .nullable(),
        binnen50m: z.number(),
        naaste: z.object({ corporatie: z.string().nullable(), afstandM: z.number() }).nullable(),
      }),
    ),
    buurt: bronSchema(
      z.object({ jaar: z.number(), koopPct: getal, corporatiePct: getal, particulierPct: getal }),
    ),
  }),
  opgehaaldOp: z.string(),
});
export type Dossier = z.infer<typeof dossierSchema>;
